<script setup lang="ts">
import { ref, computed, watch, onMounted, defineAsyncComponent, toRaw } from "vue"
import { useRouter } from "vue-router"
import { useUserStore } from "@/stores/UserStore.ts"
import { now, retry } from "@/compositions/utils.ts"
import { differenceInSeconds, parse, formatRelative } from "date-fns"
import { TZDate } from "@date-fns/tz"

const Overlay = defineAsyncComponent(() => import("@/components/Overlay.vue"))
const RevenueButton = defineAsyncComponent(() => import("@/components/TopNav/RevenueButton.vue"))

const UserStore = useUserStore()
const router = useRouter()

const timeclock = ref<any[]>([])
const hiDialog = ref(false)
const byeDialog = ref(false)
const timeBack = ref(null)
const overlay = ref(false)
const extraNote = ref("")
const pendingSignOut = ref(false)
const menu = ref()
const items = ref([
  {
    label: UserStore.user.username || "Babble-on",
    items: [
      {
        label: "Clock in",
        icon: "fas fa-clock",
        command: () => {
          punchClock()
        },
      },
      {
        separator: true,
      },
      {
        label: "Profile",
        icon: "fal fa-user-circle",
        command: () => {
          router.push({ name: "profile" })
        },
      },
      {
        label: "Timeclock",
        icon: "fal fa-history",
        command: () => {
          router.push({ name: "timeclock" })
        },
      },
      {
        separator: true,
      },
      {
        label: "Sign out",
        icon: "fal fa-power-off",
        command: () => {
          // Don't let a clocked-in PM sign out without clocking out first —
          // show the clock-out dialog, then finish signing out once they do.
          if (clockedin.value == true) {
            pendingSignOut.value = true
            byeDialog.value = true
            return
          }
          signOut()
        },
      },
    ],
  },
])

const workedToday = computed(() => {
  if (!Array.isArray(timeclock.value) || timeclock.value.length === 0) return {}
  let timestamps = toRaw(timeclock.value)
  if (timestamps.length % 2 != 0) timestamps.unshift({ date: now().split(" ")[0], time: now().split(" ")[1] })
  let totalSeconds = 0
  for (let i = 0; i < timestamps.length; i += 2) {
    const end = parse(`${timestamps[i].date} ${timestamps[i].time}`, "yyyy-MM-dd HH:mm:ss", new Date())
    const start = parse(`${timestamps[i + 1].date} ${timestamps[i + 1].time}`, "yyyy-MM-dd HH:mm:ss", new Date())
    totalSeconds += Math.abs(differenceInSeconds(start, end))
  }

  const hours = Math.round((totalSeconds / 3600) * 4) / 4

  // Return a formatted string like "2 hours 15 minutes"
  const formatted = `${hours} hour${hours != 1 ? "s" : ""} `
  return { hours: hours, formatted: formatted }
})

const clockedin = computed(() => timeclock.value?.length > 0 && timeclock.value?.length % 2 != 0)
const byeNote = computed(() => (timeBack.value ? formatRelative(TZDate.tz("America/Los_Angeles", timeBack.value), now()) : ""))
const yourLocalTime = computed(() => (timeBack.value ? formatRelative(timeBack.value, new Date()) : ""))

onMounted(async () => {
  if (UserStore.impersonate && UserStore.impersonate !== true) {
    items.value[0].items.push(
      {
        separator: true,
      },
      {
        label: `Stop impersonating ${UserStore.impersonate}`,
        icon: "fal fa-face-disguise",
        command: () => {
          UserStore.fill()
        },
      }
    )
  }

  if (UserStore.pm == false && UserStore.admin == false) {
    items.value[0].items.splice(3, 1)
    items.value[0].items.splice(0, 1)
    return
  }

  timeclock.value = await retry(() => UserStore.getTimeClock())

  if ((clockedin.value == false && UserStore.admin == false) || import.meta.env.VITE_TEST_ADMINCLOCKSIN == "true") hiDialog.value = true
  if (UserStore.admin) {
    items.value[0].items.splice(4, 0, {
      label: "User Administration",
      icon: "fal fa-users-gear",
      command: () => {
        router.push({ name: "useradmin" })
      },
    })
    items.value[0].items.splice(5, 0, {
      label: "DB Backups",
      icon: "fal fa-database",
      command: () => {
        router.push({ name: "backups" })
      },
    })
  }
})

const toggleMenu = (event: any) => {
  items.value[0].items[0].label = `Clock ${clockedin.value == true ? "out" : "in"}`
  items.value[0].label = UserStore.user.username || ""
  if (timeclock.value.length > 0) items.value[0].label += ` — ${clockedin.value == true ? `worked ${workedToday.value.formatted}` : `clocked out ${workedToday.value.formatted}`}`
  menu.value.toggle(event)
}

// Reset the pending sign-out if the clock-out dialog is dismissed without
// clocking out, so a later normal clock-out doesn't sign the user out.
watch(byeDialog, (visible) => {
  if (!visible && !overlay.value) pendingSignOut.value = false
})

async function signOut() {
  await UserStore.logout()
  router.push({ name: "login" })
}

async function punchClock() {
  if (clockedin.value == true && byeDialog.value == false) {
    byeDialog.value = true
  } else {
    const wasSigningOut = pendingSignOut.value
    byeDialog.value = false
    hiDialog.value = false
    overlay.value = true
    setTimeout(() => (overlay.value = false), 2000)
    timeclock.value = await UserStore.punchClock(UserStore.user.username, `${byeNote.value} ${extraNote.value}`.trim())
    timeBack.value = null
    if (wasSigningOut) {
      pendingSignOut.value = false
      await signOut()
    }
  }
}
</script>

<template>
  <Overlay v-if="overlay == true" :icon="'hand-wave'" />
  <RevenueButton v-if="UserStore.admin" />
  <Button type="button" outlined severity="contrast" @click="toggleMenu" aria-haspopup="true" aria-controls="overlay_menu" :class="{ glowing: clockedin }" class="neon-green">
    <UserAvatar v-if="UserStore.user.username" :username="UserStore.user.username" size="normal" shape="circle" />
    <Avatar v-else class="mr-2" size="normal" style="background-color: #ece9fc; color: #2a1261" shape="circle">
      <font-awesome-icon icon="fal fa-user" fade size="lg" />
    </Avatar>
  </Button>
  <Menu ref="menu" id="overlay_menu" :model="items" :popup="true" style="z-index: 2005">
    <template #item="{ item, props }">
      <a v-ripple class="flex items-center" v-bind="props.action">
        <font-awesome-icon :icon="item.icon" />
        <span>{{ item.label }}</span>
      </a>
    </template>
  </Menu>

  <Dialog v-model:visible="hiDialog" modal header="Clock in" class="container-sm" @keydown.enter="punchClock">
    <div class="display-4 text-secondary mb-3">Welcome back!</div>
    <h5 v-if="hiDialog && workedToday.formatted">Today, you’ve worked {{ workedToday.formatted }}</h5>

    <h4 class="float-end">Did you want to clock in?</h4>
    <Divider />
    <div class="d-flex gap-3 justify-content-end">
      <Button label="Later" severity="secondary" outlined @click="hiDialog = false" />
      <Button label="Clock in" @click="punchClock()" />
    </div>
  </Dialog>

  <Dialog v-model:visible="byeDialog" modal header="Clock out" class="container-sm">
    <div class="d-flex gap-5 justify-content-around flex-wrap flex-row-reverse">
      <DatePicker v-model="timeBack" inline showTime hourFormat="12" :minDate="new Date()" :invalid="!byeNote" :stepMinute="5" variant="filled" fluid />
      <div>
        <div class="display-6 text-secondary mb-3">Be back soon?</div>

        <div class="d-flex gap-5 flex-wrap">
          <div class="d-flex flex-column">
            <div class="text-success h5">Your time</div>
            <div class="h4">{{ yourLocalTime }}</div>
          </div>
          <div class="d-flex flex-column">
            <div class="text-secondary h5">California time</div>
            <div class="fs-5">{{ byeNote }}</div>
          </div>
        </div>
        <div>
          <TextArea v-model="extraNote" rows="2" cols="40" placeholder="Anything else you need to say…" class="mt-md-5" autoResize variant="filled" />
        </div>
      </div>
    </div>
    <Divider />
    <Button label="Clock out now" @click="punchClock()" :disabled="!byeNote" class="float-end" />
  </Dialog>
</template>
